import React, { useState, useEffect } from 'react';
import { Award, Save, Loader2, CheckCircle, Users } from 'lucide-react';
import { auth } from '../config/firebase';
import { configuracaoService, clienteService } from '../services/api';

const FidelidadeTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const [ativo, setAtivo] = useState(false);
  const [metaAgendamentos, setMetaAgendamentos] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [clientes, setClientes] = useState([]);

  useEffect(() => {
    const carregarDados = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const token = await user.getIdToken();
        const [resConfig, resClientes] = await Promise.all([
          configuracaoService.obterFidelidade(user.uid),
          clienteService.listar(token),
        ]);
        setAtivo(resConfig.data.ativo);
        setMetaAgendamentos(resConfig.data.meta_agendamentos || '');
        setMensagem(resConfig.data.mensagem || '');
        setClientes(resClientes.data || []);
      } catch (error) {
        console.error('Erro ao carregar fidelidade:', error);
      } finally {
        setLoading(false);
      }
    };
    carregarDados();
  }, []);

  const handleSalvar = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);
    try {
      const uid = auth.currentUser.uid;
      await configuracaoService.salvarFidelidade(uid, {
        ativo,
        meta_agendamentos: metaAgendamentos ? parseInt(metaAgendamentos, 10) : null,
        mensagem,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      alert('Erro ao salvar configuração de fidelidade.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <Loader2 className="animate-spin text-[#1883FF]" size={24} />
      </div>
    );
  }

  const meta = parseInt(metaAgendamentos, 10) || 0;

  return (
    <div className="animate-fade-in-up flex flex-col gap-8">
      <div>
        <div className="flex items-center gap-3 mb-6 border-b border-purple-50 pb-4">
          <Award size={18} className="text-[#1883FF]" />
          <div>
            <h3 className="text-sm font-black uppercase italic text-slate-900 m-0">Cartão Fidelidade</h3>
            <p className="text-[10px] font-semibold text-slate-500 m-0 mt-1">
              Ao atingir a meta de agendamentos, o cliente recebe automaticamente uma mensagem de prêmio no WhatsApp e a contagem reinicia.
            </p>
          </div>
        </div>

        <form onSubmit={handleSalvar} className="flex flex-col gap-5">
          <label className="flex items-center gap-2 text-xs font-black uppercase text-slate-700 cursor-pointer select-none">
            <input type="checkbox" checked={ativo} onChange={e => setAtivo(e.target.checked)} />
            Ativar cartão fidelidade
          </label>

          <div className="flex flex-col gap-1.5 text-left max-w-[240px]">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Meta de Agendamentos</label>
            <input
              type="number" min="1"
              className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
              placeholder="Ex: 10"
              value={metaAgendamentos} onChange={e => setMetaAgendamentos(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5 text-left">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Mensagem de Prêmio</label>
            <textarea
              className="w-full bg-[#F8F7FF] border border-purple-50 py-3 px-4 rounded-xl text-sm font-semibold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 min-h-[90px] resize-y font-sans"
              placeholder="Ex: Parabéns, {nome}! Você atingiu sua meta e ganhou um desconto especial na próxima visita! 🎉"
              value={mensagem} onChange={e => setMensagem(e.target.value)}
            />
            <p className="text-[10px] text-slate-400 font-semibold ml-1">Use <code>{'{nome}'}</code> para inserir o primeiro nome do cliente automaticamente.</p>
          </div>

          <button
            type="submit" disabled={saving}
            className={`w-full sm:w-auto self-start px-6 py-3.5 border-none rounded-2xl text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2.5 transition-all duration-400 cursor-pointer shadow-lg active:scale-98 disabled:opacity-60 disabled:cursor-not-allowed ${success ? 'bg-[#059669] shadow-emerald-600/20' : 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] shadow-[#6D28D9]/20 hover:opacity-90 hover:-translate-y-0.5'}`}
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : success ? <CheckCircle size={16} /> : <Save size={16} />}
            {saving ? 'Salvando...' : success ? 'Salvo!' : 'Salvar'}
          </button>
        </form>
      </div>

      <div>
        <div className="flex items-center gap-3 mb-4 border-b border-purple-50 pb-4">
          <Users size={18} className="text-[#1883FF]" />
          <h3 className="text-sm font-black uppercase italic text-slate-900 m-0">Progresso dos Clientes</h3>
        </div>
        {clientes.length === 0 ? (
          <p className="text-xs text-slate-400 font-medium">Nenhum cliente cadastrado ainda.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {clientes.map(cliente => {
              const atual = cliente.agendamentos_desde_ultimo_premio || 0;
              const pct = meta > 0 ? Math.min(100, (atual / meta) * 100) : 0;
              return (
                <div key={cliente.id} className="bg-[#F8F7FF] rounded-xl p-3.5 flex items-center justify-between gap-3">
                  <span className="text-xs font-bold text-slate-700">{cliente.nome}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#1883FF] to-[#6D28D9]" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] font-black text-slate-500">{atual}/{meta || '—'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FidelidadeTab;
